import { useState, useEffect } from 'react';
import api from '../utils/api';               // Axios instance with baseURL: http://localhost:8080
import { Link } from 'react-router-dom';
import {
  HiTrash, HiReply, HiXCircle, HiChevronDown, HiChevronUp,
  HiLocationMarker, HiPhone, HiMail, HiCreditCard, HiShoppingBag
} from 'react-icons/hi';
import { useCurrency } from '../context/CurrencyContext';
import { useCart } from '../context/CartContext';

// Used ONLY for image previews – images are not proxied
const API_BASE =  process.env.REACT_APP_API_URL;

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrash, setShowTrash] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const { formatCurrency } = useCurrency();
  const { addToCart } = useCart();

  const fetchOrders = () => {
    setLoading(true);
    // api already has baseURL: http://localhost:8080, so these paths are absolute
    const endpoint = showTrash ? '/api/orders/deleted' : '/api/orders';
    api.get(endpoint)
      .then(res => {
        const sorted = res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const unique = [];
        const seen = new Set();
        for (const order of sorted) {
          if (!seen.has(order.id)) {
            seen.add(order.id);
            unique.push(order);
          }
        }
        setOrders(unique);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders();
  }, [showTrash]);

  const handleDelete = async (orderId) => {
    if (!window.confirm('Move this order to trash?')) return;
    try {
      await api.put(`/api/orders/${orderId}/customer-delete`);
      fetchOrders();
    } catch (err) {
      alert('Failed to delete order');
    }
  };

  const handleRestore = async (orderId) => {
    try {
      await api.put(`/api/orders/${orderId}/customer-restore`);
      fetchOrders();
    } catch (err) {
      alert('Failed to restore order');
    }
  };

  const handlePermanentDelete = async (orderId) => {
    if (!window.confirm('Permanently delete this order? This CANNOT be undone.')) return;
    try {
      await api.delete(`/api/orders/${orderId}/permanent`);
      fetchOrders();
    } catch (err) {
      alert('Failed to delete order permanently');
    }
  };

  const handleReorder = (order) => {
    order.items?.forEach(item => {
      addToCart(
        { id: item.productId, name: item.productName, imageUrls: [item.image], basePrice: item.price },
        { size: item.size, color: item.color, price: item.price, stock: 10 },
        item.quantity
      );
    });
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/uploads/')) return `${API_BASE}/images/${url.replace('/uploads/', '')}`;
    return url;
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'PAID':
      case 'SHIPPING':
      case 'DELIVERED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'CANCELLED':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-[3px] border-gray-200 border-t-black rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 sm:py-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900">My Orders</h2>
        <button
          onClick={() => { setShowTrash(!showTrash); setExpandedOrder(null); }}
          className="text-sm font-medium text-gray-500 hover:text-gray-800 transition underline underline-offset-4 self-start sm:self-auto"
        >
          {showTrash ? '← Back to active orders' : 'View trash'}
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-20 bg-white border border-gray-100 rounded-2xl shadow-sm">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-gray-500 mt-4 mb-2">{showTrash ? 'Trash is empty' : 'No orders yet'}</p>
          <Link to="/products" className="text-sm font-semibold text-black underline hover:no-underline">
            Start shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map(order => {
            const isExpanded = expandedOrder === order.id;
            const address = order.shippingAddress;
            const customerName = address
              ? (address.fullName || `${address.firstName || ''} ${address.lastName || ''}`).trim()
              : 'N/A';

            return (
              <div key={order.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                {/* Order header (always visible) */}
                <div className="p-5 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-mono text-gray-400 uppercase tracking-wider">
                        Order #{order.id.substring(0, 8)}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        {new Date(order.createdAt).toLocaleDateString('en-NG', {
                          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      {customerName && (
                        <p className="text-sm font-medium text-gray-700 mt-1">{customerName}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 self-start sm:self-auto">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${statusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {order.paymentMethod?.toUpperCase() || 'N/A'}
                      </span>

                      {/* Active order actions */}
                      {!showTrash && (
                        <>
                          <button
                            onClick={() => handleReorder(order)}
                            className="text-gray-400 hover:text-blue-500 transition p-1.5 rounded-full hover:bg-blue-50"
                            title="Reorder items"
                          >
                            <HiShoppingBag size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="text-gray-400 hover:text-rose-500 transition p-1.5 rounded-full hover:bg-rose-50"
                            title="Move to trash"
                          >
                            <HiTrash size={16} />
                          </button>
                        </>
                      )}

                      {/* Trash actions */}
                      {showTrash && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRestore(order.id)}
                            className="text-gray-400 hover:text-emerald-500 transition p-1.5 rounded-full hover:bg-emerald-50"
                            title="Restore order"
                          >
                            <HiReply size={16} />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(order.id)}
                            className="text-gray-400 hover:text-red-600 transition p-1.5 rounded-full hover:bg-red-50"
                            title="Delete forever"
                          >
                            <HiXCircle size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expand toggle (only on active orders, hide in trash) */}
                  {!showTrash && (
                    <button
                      onClick={() => toggleExpand(order.id)}
                      className="mt-3 flex items-center gap-1 text-xs text-gray-500 hover:text-black"
                    >
                      {isExpanded ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />}
                      {isExpanded ? 'Hide details' : 'View details'}
                    </button>
                  )}
                </div>

                {/* Expanded details (shipping address, payment, items) */}
                {isExpanded && !showTrash && (
                  <div className="border-t border-gray-100 px-5 sm:px-6 py-4 bg-gray-50/50">
                    {/* Shipping Address */}
                    {address && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Shipping Address
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <HiLocationMarker className="text-gray-400 flex-shrink-0" size={16} />
                            <span>
                              {[address.address, address.city, address.state]
                                .filter(Boolean).join(', ')}
                            </span>
                          </div>
                          {address.phone && (
                            <div className="flex items-center gap-2">
                              <HiPhone className="text-gray-400 flex-shrink-0" size={16} />
                              <span>{address.phone}</span>
                            </div>
                          )}
                          {address.email && (
                            <div className="flex items-center gap-2">
                              <HiMail className="text-gray-400 flex-shrink-0" size={16} />
                              <span>{address.email}</span>
                            </div>
                          )}
                          {order.paymentMethod && (
                            <div className="flex items-center gap-2">
                              <HiCreditCard className="text-gray-400 flex-shrink-0" size={16} />
                              <span className="capitalize">{order.paymentMethod}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Items */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Items
                      </h4>
                      <ul className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <li key={`${item.productId}-${idx}`} className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              {item.image ? (
                                <img src={getImageUrl(item.image)} alt={item.productName} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-bold">
                                  {item.productName?.charAt(0) || 'P'}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.productName}</p>
                              <p className="text-xs text-gray-400">
                                {item.size && `${item.size}`}{item.color && ` / ${item.color}`} · Qty: {item.quantity}
                              </p>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                              {formatCurrency(item.price * item.quantity)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Footer: total (always visible) */}
                <div className="px-5 sm:px-6 py-3 border-t border-gray-50 flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    {order.items?.length} item{order.items?.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {formatCurrency(order.total)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}